import { action, computed, decorate, observable } from "mobx";
import _ from "lodash";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  CUSTOM_CLIP_TIME_FORMAT,
  MEDIA_SOURCE,
  TRIPLE_A_SPORT_ID,
  DOUBLE_A_SPORT_ID,
  HIGH_A_SPORT_ID,
  SINGLE_A_SPORT_ID
} from "../../utils/constants";

const MILB_SPORT_IDS = [
  TRIPLE_A_SPORT_ID,
  DOUBLE_A_SPORT_ID,
  HIGH_A_SPORT_ID,
  SINGLE_A_SPORT_ID
];
const MILB_VIDEO_MODE = "MILB";
const MLB_VIDEO_MODE = "MLB";

const customParseFormat = require("dayjs/plugin/customParseFormat");
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

class CreateCustomClipStore {
  constructor(
    pitsApi,
    jaredApi,
    sportyApi,
    sportyGateway,
    statsApi,
    myCustomClipsStore,
    allCustomClipsStore,
    authStore
  ) {
    this.reset();
    this.fetching = false;
    this.saving = false;
    this.pitsApi = pitsApi;
    this.jaredApi = jaredApi;
    this.sportyApi = sportyApi;
    this.sportyGateway = sportyGateway;
    this.statsApi = statsApi;
    this.myCustomClipsStore = myCustomClipsStore;
    this.allCustomClipsStore = allCustomClipsStore;
    this.authStore = authStore;
  }

  reset() {
    this.assets = [];
    this.selectedAsset = null;
    this.gamePk = null;
    this.sportId = null;
    this.clipDescription = "";
    this.descriptionError = false;
    this.atBatIndex = null;
  }

  async getPlayInfo(playId) {
    return this.pitsApi.getCommonFields([playId]);
  }

  async initializeAdHoc(feedType, assetName, playbackUrl, assetId) {
    // If assetName and playbackUrl are passed then using backdoor to cut ad hoc feed instead of game feed
    // For the time being we will be using the legacy jaredApi for clipping adhoc Cerebro assets
    // once the assetId can be sourced for those events we will use the new mutation in sportyApi
    // TODO: MEDIASVC-2797
    this.assets = [
      {
        id: assetId,
        name: assetName,
        source: MEDIA_SOURCE.CEREBRO,
        playbackUrl,
        feedType
      }
    ];

    this.gamePk = -1;
    await this.setClipFeedByAssetName(assetName);
    this._initializeTimes();
  }

  async initializeAtBat(gamePk, date, start, end, assetName, atBatIndex) {
    if (!gamePk || !assetName || !start || !end || atBatIndex === null) {
      toast.error("Missing data to cut a custom clip off at bat");
      return;
    }

    this.fetching = true;
    this.atBatIndex = atBatIndex;
    this.absoluteStartTime = start;
    this.absoluteEndTime = end;

    try {
      await this._initializeGame(gamePk, date);

      // When editing an at bat we don't want to reload the asset every time we select a new at bat
      if (this.selectedAsset == null) {
        await this.setClipFeedByAssetName(assetName);
      } else {
        await this._initializeAbsoluteTimes(assetName);
      }
    } catch (e) {
      console.log(e);
      this.reset();
      toast.error(`Failed to initialize custom clipping`);
      throw e;
    } finally {
      this.fetching = false;
    }
  }

  async initialize(gamePk, date, start, end, feed, assetName) {
    // GamePk is the minimum required field to load or default the data we need
    if (!gamePk) {
      toast.error("GamePk required to cut a custom clip");
      return;
    }

    this.fetching = true;
    this.absoluteStartTime = start;
    this.absoluteEndTime = end;
    let startSeconds,
      endSeconds = 0;

    try {
      await this._initializeGame(gamePk, date);
      if (assetName && start && end) {
        // 1. Editing existing custom clip we have asset name but times are relative
        ({ startSeconds, endSeconds } = this._convertToSeconds(start, end));
      } else if (feed && start && end) {
        // 2. Editing a sporty play we only have feed and play times are in UTC
        ({
          assetName,
          startSeconds,
          endSeconds
        } = await this._getAssetAndSeconds(gamePk, feed, start, end));
      }

      if (!assetName) {
        await this.setClipFeedByFeedType(feed);
      } else {
        await this.setClipFeedByAssetName(assetName);
      }

      this._initializeTimes(startSeconds, endSeconds);
    } catch (e) {
      console.log(e);
      this.reset();
      toast.error(`Failed to initialize custom clipping`);
      throw e;
    } finally {
      this.fetching = false;
    }
  }

  async _initializeGame(gamePk, date) {
    // Type coersion fine here
    if (gamePk === this.gamePk) {
      return;
    }

    // Initializing new game so need to load feeds
    this.gamePk = gamePk;
    const game = await this.statsApi.getGame(this.gamePk);
    const existingGame = game.dates.length > 0 ? game.dates[0].games[0] : null;
    if (existingGame) {
      this.sportId = existingGame["teams"]["home"]["team"]["sport"]["id"];
    }

    // Get available assets for game
    const assets = await this.sportyGateway.getGameMedia(gamePk, date);

    if (!assets || assets.length === 0) {
      toast.warn(`No assets for game ${gamePk}`);
      this.selectedAsset = null;
      return;
    }

    this.assets = assets.sort((a, b) => {
      // Sort so that CONTENT_REGISTRY assets come first
      const isAContentRegistry =
        a.source === MEDIA_SOURCE.CONTENT_REGISTRY ? -1 : 0;
      const isBContentRegistry =
        b.source === MEDIA_SOURCE.CONTENT_REGISTRY ? -1 : 0;
      return isAContentRegistry - isBContentRegistry;
    });
  }

  async _initializeAbsoluteTimes(assetName) {
    const { startSeconds, endSeconds } = await this._getSeconds(
      assetName,
      this.absoluteStartTime,
      this.absoluteEndTime
    );
    this._initializeTimes(startSeconds, endSeconds);
  }

  _initializeTimes(startSeconds = 0, endSeconds = 0) {
    if (!this.selectedAsset) {
      return;
    }

    this.officialStartTime = dayjs(this.selectedAsset.videoGameDate)
      .utc()
      .startOf("day")
      .add(startSeconds, "s");
    this.adjustedStartTime = this.officialStartTime;

    this.officialEndTime = dayjs(this.selectedAsset.videoGameDate)
      .utc()
      .startOf("day")
      .add(endSeconds, "s");
    this.adjustedEndTime = this.officialEndTime;
  }

  _convertToSeconds(start, end) {
    let startSeconds = 0,
      endSeconds = 0;

    if (start && dayjs(start, CUSTOM_CLIP_TIME_FORMAT, true).isValid()) {
      // Need to convert HH:mm:ss.SSS format to number of seconds
      const dayStartSeconds = dayjs()
        .utc()
        .startOf("day")
        .unix();
      startSeconds =
        dayjs(start, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
      endSeconds =
        dayjs(end, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
    }

    return { startSeconds, endSeconds };
  }

  async _getAssetAndSeconds(gamePk, feed, start, end) {
    let startSeconds,
      endSeconds = 0;
    let assetName;

    if (start && dayjs(start, CUSTOM_CLIP_TIME_FORMAT, true).isValid()) {
      // Need to convert HH:mm:ss.SSS format to number of seconds
      const dayStartSeconds = dayjs()
        .utc()
        .startOf("day")
        .unix();
      startSeconds =
        dayjs(start, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
      endSeconds =
        dayjs(end, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
    } else if (start && dayjs(start).isValid()) {
      // We got a full date time and we need to call out to Jared to figure out where this lands in the feed
      const assetTimings = await this.jaredApi.getAssetAndTimeInFeed(
        gamePk,
        feed,
        start,
        end
      );
      assetName = assetTimings.assetName;
      startSeconds = assetTimings.startSeconds;
      endSeconds = assetTimings.endSeconds;
    }
    return { assetName, startSeconds, endSeconds };
  }

  async _getSeconds(assetName, start, end) {
    let startSeconds,
      endSeconds = 0;

    if (start && dayjs(start, CUSTOM_CLIP_TIME_FORMAT, true).isValid()) {
      // Need to convert HH:mm:ss.SSS format to number of seconds
      const dayStartSeconds = dayjs()
        .utc()
        .startOf("day")
        .unix();
      startSeconds =
        dayjs(start, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
      endSeconds =
        dayjs(end, CUSTOM_CLIP_TIME_FORMAT)
          .utc()
          .unix() - dayStartSeconds;
    } else if (start && dayjs(start).isValid()) {
      // We got a full date time, and we need to call out to Jared to figure out where this lands in the feed
      const assetTimings = await this.jaredApi.getTimeInFeed(
        assetName,
        start,
        end,
        this._getMode()
      );
      startSeconds = assetTimings.startSeconds;
      endSeconds = assetTimings.endSeconds;
    }
    return { startSeconds, endSeconds };
  }

  setClipDescription(description) {
    this.clipDescription = description;
    this.descriptionError = this.clipDescription === "";
  }

  async setClipFeedByAssetName(assetName) {
    this.selectedAsset =
      (assetName &&
        _.find(
          this.assets,
          a => a.name.toUpperCase() === assetName.toUpperCase()
        )) ||
      this.assets[0];
    this.assetName = assetName;

    if (this.absoluteStartTime) {
      await this._initializeAbsoluteTimes(this.assetName);
    } else {
      this.resetClipTiming();
    }
  }

  async setClipFeedByFeedType(feedType) {
    this.selectedAsset =
      (feedType &&
        _.find(
          this.assets,
          a => a.feedType.toUpperCase() === feedType.toUpperCase()
        )) ||
      this.assets[0];
    if (this.selectedAsset) {
      this.assetName = this.selectedAsset.name;
    }

    if (this.absoluteStartTime) {
      await this._initializeAbsoluteTimes(this.assetName);
    } else {
      this.resetClipTiming();
    }
  }

  adjustClipStart(seconds) {
    // There is a bug when adding seconds to a dayjs object if the timezone is not set
    // Timezone will change on daylight savings and be an hour off.
    this.adjustedStartTime = dayjs(this.selectedAsset.videoGameDate)
      .utc()
      .startOf("day")
      .add(seconds, "s");
  }

  adjustClipEnd(seconds) {
    this.adjustedEndTime = dayjs(this.selectedAsset.videoGameDate)
      .utc()
      .startOf("day")
      .add(seconds, "s");
  }

  resetClipTiming() {
    this.adjustedStartTime = this.officialStartTime;
    this.adjustedEndTime = this.officialEndTime;
  }

  get clipStartOffset() {
    if (this.adjustedStartTime) {
      return this.adjustedStartTime.format(CUSTOM_CLIP_TIME_FORMAT);
    }

    return "";
  }

  get clipStartOffsetSeconds() {
    return this._calcSecondsFromFeedStart(this.adjustedStartTime);
  }

  get clipEndOffset() {
    if (this.adjustedEndTime) {
      return this.adjustedEndTime.format(CUSTOM_CLIP_TIME_FORMAT);
    }
    return "";
  }

  get clipEndOffsetSeconds() {
    return this._calcSecondsFromFeedStart(this.adjustedEndTime);
  }

  _calcSecondsFromFeedStart(time) {
    if (time) {
      const adjustedSeconds = time.valueOf() / 1000.0;
      const initialSeconds =
        dayjs(this.selectedAsset.videoGameDate)
          .utc()
          .startOf("day")
          .valueOf() / 1000.0;
      return adjustedSeconds - initialSeconds;
    }
    return 0.0;
  }

  _getMode() {
    return this.sportId && MILB_SPORT_IDS.includes(this.sportId)
      ? MILB_VIDEO_MODE
      : MLB_VIDEO_MODE;
  }

  async saveClip(audioTrackName, audioTrackOffset) {
    if (!this.validateClip()) {
      return false;
    }

    this.saving = true;

    try {
      let response;
      if (this.selectedAsset.source.toUpperCase() === MEDIA_SOURCE.CEREBRO) {
        response = await this.jaredApi.cutCustomClip({
          assetName: this.selectedAsset.name,
          startTime: this.clipStartOffset,
          endTime: this.clipEndOffset,
          thumbnail: true,
          mode: this._getMode()
        });
      } else {
        let audioTracks = [];
        // Setup audio track with offset
        // Offset to endpoint is inverse of the ui
        // Positive advances the track
        // Negative delays the track.
        if (audioTrackName) {
          audioTracks = [
            {
              name: audioTrackName,
              priority: 0,
              offset: audioTrackOffset
            }
          ];
        }

        response = await this.sportyGateway.createCustomClip({
          id: this.selectedAsset.id,
          source: this.selectedAsset.source,
          start: this.clipStartOffset,
          end: this.clipEndOffset,
          userId: this.authStore.user.sub,
          thumbnail: true,
          mode: this._getMode(),
          audioTracks: audioTracks
        });
      }

      if (!response.success) {
        toast.error("Failed to create custom video", {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      await this.sportyApi.saveUserCustomClip({
        gamePk: this.gamePk,
        feedType: this.selectedAsset.feedType.toUpperCase(),
        url: response.videoUrl,
        description: this.clipDescription || `Untitled: ` + dayjs().format(),
        startTime: this.clipStartOffset,
        endTime: this.clipEndOffset,
        thumbnailUri: response.thumbnailUri,
        assetName: this.assetName
      });

      this.clipDescription = "";

      // Fire off requests to reload the clips asynchronously
      this.myCustomClipsStore.refreshClips();
      this.allCustomClipsStore.refreshClips();

      return true;
    } finally {
      this.saving = false;
    }
  }

  validateClip() {
    this.descriptionError = this.clipDescription === "";
    return !this.descriptionError;
  }
}

decorate(CreateCustomClipStore, {
  assets: observable,
  selectedAsset: observable,
  selectedAudioTrack: observable,
  clipDescription: observable,
  descriptionError: observable,
  officialStartTime: observable,
  adjustedStartTime: observable,
  officialEndTime: observable,
  adjustedEndTime: observable,
  atBatIndex: observable,
  absoluteStartTime: observable,
  absoluteEndTime: observable,
  gamePk: observable,
  fetching: observable,
  saving: observable,
  resetClipTiming: action,
  reset: action,
  clipStartOffset: computed,
  clipStartOffsetSeconds: computed,
  clipEndOffset: computed,
  clipEndOffsetSeconds: computed
});

export default CreateCustomClipStore;



// WEBPACK FOOTER //
// ./src/stores/customClips/CreateCustomClipStore.js